import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, IconButton, Tabs, Tab, Box, Typography, CircularProgress, Button } from '@mui/material';
import { X, RefreshCw } from 'lucide-react';
import VersionBadge from './VersionBadge';
import AvatarImage from './AvatarImage';
import { fetchCodeIndexVersionsChangelog, VersionEntry } from '../services/codeIndexService';
import { formatDateTime } from '../lib/utils';
import { showConfirmModal, showInfoModal } from './ui/ConfirmDialog';

export interface PreviewVersionRequest {
	versionId: string;
	versionNumber: string;
}

export interface VersionsDrawerProps {
	open: boolean;
	onClose: () => void;
	entityId?: string;
	entityType?: 'codesheet' | 'other';
	rootPath?: string;
	onPreviewVersion?: (request: PreviewVersionRequest) => Promise<boolean | void>;
	activePreviewVersionId?: string | null;
	previewLoadingVersionId?: string | null;
	previewBlockedReason?: string | null;
}

interface ChangelogEntry {
	versionNumber: string;
	date: string;
	author: string;
	authorId?: string;
	description: string;
	versionType: 'current' | 'major' | 'minor';
	versionBranch: string;
}

interface VersionListItem {
	versionId: string;
	versionNumber: string;
	date: string;
	description: string;
	authorName: string;
	authorAvatarUri: string | null;
	versionType: 'current' | 'major' | 'minor';
	versionBranch: string;
	isCurrentVersion: boolean;
}

const versionBorderColors = {
	current: '#EFAD29',
	minor: '#7CB342',
	major: '#2687DC'
} as const;

// Helper function to extract version branch from semVer
const extractVersionBranch = (semVer: string): string => {
	const match = semVer.match(/^(\d+\.\d+)/);
	return match ? match[1] : semVer;
};

// Helper function to determine version type
const getVersionType = (version: VersionEntry): 'current' | 'major' | 'minor' => {
	if (version.isCurrentVersion) return 'current';
	if (version.isMajor) return 'major';
	return 'minor';
};

// Helper function to format date from API
const formatDate = (isoDate: string): string => {
	try {
		return formatDateTime(isoDate);
	} catch {
		return isoDate;
	}
};

const mapVersionToListItem = (version: VersionEntry): VersionListItem => ({
	versionId: version.versionId,
	versionNumber: version.semVer,
	date: formatDate(version.createdAt),
	description: version.description || version.changeSet.description || 'No description available',
	authorName: version.changeSet.createdBy.name,
	authorAvatarUri: version.changeSet.createdBy.avatarUri ?? null,
	versionType: getVersionType(version),
	versionBranch: extractVersionBranch(version.semVer),
	isCurrentVersion: version.isCurrentVersion
});

const mapVersionToChangelogEntries = (version: VersionEntry): ChangelogEntry[] => {
	const versionType = getVersionType(version);
	const versionBranch = extractVersionBranch(version.semVer);
	const author = version.changeSet.createdBy.name;
	const date = formatDate(version.createdAt);

	if (version.changes.length === 0) {
		return [{
			versionNumber: version.semVer,
			date,
			author,
			authorId: version.changeSet.createdBy.id,
			description: version.changeSet.description || version.description || 'No changes',
			versionType,
			versionBranch
		}];
	}

	return version.changes.map((change) => {
		const changeTypeLabel = change.changeType || 'Modified';
		const description = change.path
			? `${changeTypeLabel} ${change.nodeType}: ${change.path}`
			: `${changeTypeLabel} ${change.nodeType}`;

		return {
			versionNumber: version.semVer,
			date,
			author,
			authorId: version.changeSet.createdBy.id,
			description,
			versionType,
			versionBranch
		};
	});
};

const dedupeVersionsById = (versions: Array<VersionEntry | null | undefined>): VersionEntry[] => {
	const seenVersionIds = new Set<string>();
	const uniqueVersions: VersionEntry[] = [];

	versions.forEach((version) => {
		if (!version?.versionId || seenVersionIds.has(version.versionId)) {
			return;
		}

		seenVersionIds.add(version.versionId);
		uniqueVersions.push(version);
	});

	return uniqueVersions;
};

export default function VersionsDrawer({
	open,
	onClose,
	entityId,
	entityType = 'codesheet',
	rootPath = 'Root',
	onPreviewVersion,
	activePreviewVersionId = null,
	previewLoadingVersionId = null,
	previewBlockedReason = null
}: VersionsDrawerProps) {
	const [activeTab, setActiveTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [versionFilter, setVersionFilter] = useState<string | null>(null);
	const [changelogData, setChangelogData] = useState<ChangelogEntry[]>([]);
	const [currentVersionData, setCurrentVersionData] = useState<VersionListItem | null>(null);
	const [versionListData, setVersionListData] = useState<VersionListItem[]>([]);

	const handlePreviewVersion = async (item: VersionListItem) => {
		if (!onPreviewVersion) {
			return;
		}

		if (previewBlockedReason) {
			await showInfoModal({
				title: 'Preview unavailable',
				description: previewBlockedReason,
				confirmText: 'OK'
			});
			return;
		}

		const confirmed = await showConfirmModal({
			title: 'Preview Version',
			description: `You are about to preview version ${item.versionNumber}. The Codesheet table will temporarily reflect this version's data. This will not change the saved current version.`,
			confirmText: 'Preview',
			cancelText: 'Cancel'
		});

		if (!confirmed) {
			return;
		}

		try {
			const result = await onPreviewVersion({
				versionId: item.versionId,
				versionNumber: item.versionNumber
			});

			if (result !== false) {
				onClose();
			}
		} catch (err) {
			console.error('Error previewing version:', err);
		}
	};

	// Load version data function (extracted for retry functionality)
	const loadVersionData = useCallback(async () => {
		if (!entityId) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await fetchCodeIndexVersionsChangelog(entityId);
			const currentVersion = response.currentVersion ?? null;
			const historicalVersions = Array.isArray(response.versions) ? response.versions : [];
			const versionsForChangelog = dedupeVersionsById([currentVersion, ...historicalVersions]);

			setChangelogData(
				versionsForChangelog.flatMap((version) => mapVersionToChangelogEntries(version))
			);
			setCurrentVersionData(currentVersion ? mapVersionToListItem(currentVersion) : null);

			const historicalVersionItems: VersionListItem[] = [];
			const seenHistoricalVersionIds = new Set<string>();

			historicalVersions.forEach((version) => {
				if (!version?.versionId) {
					return;
				}

				if (currentVersion?.versionId === version.versionId) {
					return;
				}

				if (seenHistoricalVersionIds.has(version.versionId)) {
					return;
				}

				seenHistoricalVersionIds.add(version.versionId);
				historicalVersionItems.push(mapVersionToListItem(version));
			});

			setVersionListData(historicalVersionItems);
		} catch (err) {
			console.error('Error fetching version data:', err);
			setError('Failed to load version data. Please try again.');
		} finally {
			setLoading(false);
		}
	}, [entityId]);

	// Fetch data from API when drawer opens
	useEffect(() => {
		if (open && entityId) {
			loadVersionData();
		}
	}, [open, entityId, loadVersionData]);

	useEffect(() => {
		if (!open) {
			return;
		}

		setActiveTab(0);
		setVersionFilter(null);
	}, [open]);

	const renderVersionCard = (item: VersionListItem, options?: { emphasizeCurrent?: boolean }) => {
		const isCurrent = item.isCurrentVersion;
		const isViewingPreview = activePreviewVersionId === item.versionId;
		const isPreviewLoading = previewLoadingVersionId === item.versionId;
		const emphasizeCurrent = Boolean(options?.emphasizeCurrent);

		return (
			<div
				key={item.versionId}
				className={`bg-white rounded-lg overflow-hidden relative ${emphasizeCurrent ? 'shadow-sm' : ''}`}
				style={{
					border: `2px solid ${versionBorderColors[item.versionType]}`
				}}
			>
				{emphasizeCurrent && (
					<div className="px-3 pt-3">
						<span className="inline-flex items-center rounded-full bg-[#FFF7E0] px-2.5 py-1 text-[11px] font-medium text-[#9A6700]">
							Current version
						</span>
					</div>
				)}

				<div className="flex items-center justify-between px-3 pt-3 pb-2">
					<div className="flex items-center gap-10">
						<div className="flex flex-col gap-1">
							<div className="scale-150 origin-left">
								<VersionBadge
									version={item.versionNumber}
									variant={item.versionType}
								/>
							</div>
						</div>
						<span className="text-[13px] text-gray-500 flex-shrink-0">
							{item.date}
						</span>
					</div>
					<div className="flex items-center gap-2">
						<div className="max-w-[140px] text-right">
							<div className="truncate text-[12px] text-gray-600 leading-tight" title={item.authorName}>
								{item.authorName}
							</div>
						</div>
						<AvatarImage
							name={item.authorName}
							filename={item.authorAvatarUri}
							size={32}
						/>
					</div>
				</div>

				<div className="border-t border-gray-300 mx-3"></div>

				<div className="px-3 py-2">
					<div className="text-[11px] text-gray-500 font-medium mb-1.5">
						Version Description
					</div>
					<div className="text-[16px] text-gray-700 leading-relaxed" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 300 }}>
						{item.description}
					</div>
				</div>

				<div className="border-t border-gray-300 mx-3"></div>

				<div className="flex items-center justify-between gap-3 px-3 py-2">
					<div className="min-h-[36px] flex items-center">
						{isCurrent && !emphasizeCurrent && (
							<span className="inline-flex items-center rounded-full bg-[#FFF7E0] px-2.5 py-1 text-[11px] font-medium text-[#9A6700]">
								Current version
							</span>
						)}
					</div>
					<div className="flex justify-end gap-2">
						{onPreviewVersion && !isCurrent && (
							<button
								type="button"
								className={`px-3 h-9 rounded-md text-sm font-medium border transition-colors ${
									isViewingPreview
										? 'bg-[#E3F2FD] text-[#1D5176] border-[#1D5176] cursor-default'
										: 'bg-white text-[#1D5176] border-[#1D5176] hover:bg-[#F2F6F9]'
								} ${isPreviewLoading ? 'opacity-70 cursor-wait' : ''}`}
								disabled={isViewingPreview || isPreviewLoading}
								onClick={(e) => {
									e.stopPropagation();
									void handlePreviewVersion(item);
								}}
							>
								{isViewingPreview ? 'Viewing' : isPreviewLoading ? 'Loading...' : 'Preview'}
							</button>
						)}
						<button
							type="button"
							className="px-3 h-9 rounded-md text-sm font-medium border border-[#1D5176] bg-[#1D5176] text-white transition-colors hover:opacity-90"
							onClick={(e) => {
								e.stopPropagation();
								setVersionFilter(item.versionBranch);
								setActiveTab(1);
							}}
						>
							View changelog
						</button>
					</div>
				</div>
			</div>
		);
	};

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			sx={{ '& .MuiDrawer-paper': { width: '34vw', minWidth: 360, maxWidth: 560, boxSizing: 'border-box' } }}
		>
			<div className="h-full flex flex-col bg-white">
				{/* Top accent bar */}
				<div className="bg-[#E3F2FD] h-1"></div>

				{/* Header */}
				<div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
					<div className="flex items-center gap-2">
						<Typography variant="h5" className="font-semibold text-gray-900">
							Versions
						</Typography>
						<Typography variant="body2" className="text-gray-500 text-sm">
							{rootPath} /
						</Typography>
					</div>
					<IconButton onClick={onClose} className="text-gray-500 hover:text-gray-700">
						<X className="h-5 w-5" />
					</IconButton>
				</div>

				{/* Tabs */}
				<Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white', px: 2 }}>
					<Tabs
						value={activeTab}
						onChange={(_, v) => setActiveTab(v)}
						sx={{
							'& .MuiTab-root': {
								textTransform: 'none',
								fontWeight: 500,
								fontSize: '15px',
								color: '#6B7280',
								minHeight: '48px',
								padding: '10px 20px'
							},
							'& .Mui-selected': { color: '#1D5176', fontWeight: 600 },
							'& .MuiTabs-indicator': { backgroundColor: '#1D5176', height: '2px' },
						}}
					>
						<Tab label="List of versions" />
						<Tab label="Changelog" />
					</Tabs>
				</Box>

				{/* Content */}
				<div className="flex-1 overflow-auto bg-white">
					{loading ? (
						<div className="flex justify-center items-center h-full">
							<CircularProgress size={32} />
						</div>
					) : error ? (
						<div className="flex flex-col items-center justify-center h-full text-gray-500 py-12 gap-4">
							<Typography variant="body2" className="text-center text-red-500">
								{error}
							</Typography>
							<Button
								variant="contained"
								startIcon={<RefreshCw className="w-4 h-4" />}
								onClick={loadVersionData}
								sx={{
									backgroundColor: '#1D5176',
									'&:hover': {
										backgroundColor: '#164159'
									},
									textTransform: 'none',
									fontWeight: 500
								}}
							>
								Retry
							</Button>
						</div>
					) : !entityId ? (
						<div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
							<Typography variant="body2" className="text-center">
								No Code Sheet selected
							</Typography>
						</div>
					) : activeTab === 0 ? (
						// List View
						<div className="p-4 space-y-3">
							{!currentVersionData && versionListData.length === 0 ? (
								<div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
									<Typography variant="body2" className="text-center">
										No versions available
									</Typography>
								</div>
							) : (
								<>
									{currentVersionData && (
										<div data-testid="current-version-section" className="space-y-3">
											<div className="px-1">
												<div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#9A6700]">
													Current version
												</div>
												<div className="text-sm text-gray-500 mt-1">
													Active version currently shown in the codesheet.
												</div>
											</div>
											<div className="rounded-xl border border-[#F4D58D] bg-[#FFF9EC] p-3">
												{renderVersionCard(currentVersionData, { emphasizeCurrent: true })}
											</div>
										</div>
									)}

									{currentVersionData && versionListData.length > 0 && (
										<div className="py-1">
											<div className="border-t border-dashed border-gray-200" />
										</div>
									)}

									{versionListData.length > 0 && (
										<div data-testid="previous-versions-section" className="space-y-3">
											<div className="px-1">
												<div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
													Previous versions
												</div>
												<div className="text-sm text-gray-500 mt-1">
													Historical versions available for review and preview.
												</div>
											</div>
											{versionListData.map((item) => renderVersionCard(item))}
										</div>
									)}
								</>
							)}
						</div>
					) : (
						// Changelog View with Timeline
						<div className="relative">
							{/* Filter Banner */}
							{versionFilter && (
								<div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between relative z-10">
									<span className="text-sm text-blue-700">
										Showing changes for version branch: <strong>{versionFilter}.x</strong>
									</span>
									<button
										onClick={() => setVersionFilter(null)}
										className="text-blue-700 hover:text-blue-900 text-sm underline"
									>
										Clear filter
									</button>
								</div>
							)}
							
							{(() => {
								const filteredData = versionFilter 
									? changelogData.filter(entry => entry.versionBranch === versionFilter)
									: changelogData;
								
								if (filteredData.length === 0) {
									return (
										<div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
											<Typography variant="body2" className="text-center">
												No changelog entries available
											</Typography>
										</div>
									);
								}

								// Get unique branches and sort them
								const allBranches = Array.from(new Set(changelogData.map(e => e.versionBranch))).sort();
								
								// Dynamically assign colors to branches
								const availableColors = ['#2687DC', '#7CB342', '#EFAD29', '#9C27B0', '#FF9800', '#00BCD4'];
								const branchColors: Record<string, string> = {};
								allBranches.forEach((branch, idx) => {
									branchColors[branch] = availableColors[idx % availableColors.length];
								});
								
								// Left padding offset for the entire timeline
								const leftPaddingOffset = 24; // 24px additional padding
								
								// Calculate horizontal position for each branch
								const branchPositions = allBranches.reduce((acc, branch, idx) => {
									acc[branch] = leftPaddingOffset + 8 + (idx * 16); // Add offset to base position
									return acc;
								}, {} as Record<string, number>);

								// Group entries by their vertical index for line segments
								const branchRanges = allBranches.map(branch => {
									const entriesInBranch = filteredData
										.map((entry, idx) => ({ entry, idx }))
										.filter(({ entry }) => entry.versionBranch === branch);
									
									if (entriesInBranch.length === 0) return null;
									
									return {
										branch,
										firstIdx: entriesInBranch[0].idx,
										lastIdx: entriesInBranch[entriesInBranch.length - 1].idx,
										color: branchColors[branch as keyof typeof branchColors] || '#999'
									};
								}).filter(Boolean);

								return (
									<>
										{/* Branch Lines */}
										{branchRanges.map((range) => {
											if (!range) return null;
											const leftPos = branchPositions[range.branch];
											return (
												<div
													key={range.branch}
													className="absolute top-0 bottom-0 w-[2px] z-0"
													style={{
														left: `${leftPos}px`,
														backgroundColor: range.color,
														opacity: 0.4
													}}
												/>
											);
										})}

										{filteredData.map((entry, index) => {
											const circleColors = {
												current: '#EFAD29',
												minor: '#7CB342',
												major: '#2687DC'
											};
											const leftPosition = branchPositions[entry.versionBranch] || 8;
											
											return (
												<div 
													key={index} 
													className="relative flex items-center border-b border-gray-200 last:border-b-0"
													style={{ minHeight: '56px' }}
												>
													{/* Timeline Circle */}
													<div
														className="absolute w-[7px] h-[7px] rounded-full z-10"
														style={{ 
															left: `${leftPosition - 3.5}px`,
															backgroundColor: circleColors[entry.versionType]
														}}
													></div>

													{/* Left Column: Version Badge and Date */}
													<div className="flex-shrink-0 pr-3 py-2" style={{ width: '184px', paddingLeft: '72px' }}>
														{/* Version Badge */}
														<VersionBadge
															version={entry.versionNumber}
															variant={entry.versionType}
														/>
														{/* Date */}
														<div className="text-[10px] text-gray-400 mt-1 leading-tight">
															{entry.date}
														</div>
													</div>

													{/* Right Column: Author and Description */}
													<div className="flex-1 py-2 border-l border-gray-200 pl-4">
														<div className="text-[12px] text-gray-600 leading-tight">
															{entry.author}
														</div>
														<div className="text-[13px] text-gray-800 leading-tight mt-0.5">
															{entry.description}
														</div>
													</div>
												</div>
											);
										})}
									</>
								);
							})()}
						</div>
					)}
				</div>

				{/* Footer - Create new version button (only on List tab) */}
				{activeTab === 0 && (
					<div className="border-t border-gray-200 p-4 bg-white flex justify-end">
						<button
							disabled
							className="px-5 py-2.5 rounded-md text-sm font-medium text-white transition-colors cursor-not-allowed opacity-50"
							style={{ backgroundColor: '#1D5176' }}
						>
							Create new version
						</button>
					</div>
				)}
			</div>
		</Drawer>
	);
}
